import React, { useState, useCallback, useEffect } from 'react';
import { Activity, Moon, Sun } from 'lucide-react';
import ParameterControls from './components/ParameterControls';
import ResultsDisplay from './components/ResultsDisplay';
import DatasetInfo from './components/DatasetInfo';
import BiasAnalysis from './components/BiasAnalysis';
import ExperimentManager from './components/ExperimentManager';
import ConfusionMatrix from './components/ConfusionMatrix';
import StreamingProgress from './components/StreamingProgress';
import { 
  SimulationResults, 
  SimulationRequest, 
  Experiment, 
  ReasoningEffort, 
  FewShotExample,
  AIResponse,
  StreamingUpdate
} from './types/api';
import { apiService } from './services/api';

function App() {
  // Theme state
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') === 'dark' ||
        (!localStorage.getItem('theme') && window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
    return false;
  });

  // Apply dark mode class to document
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  // Basic simulation parameters
  const [sampleSize, setSampleSize] = useState(30);
  const [selectedVariables, setSelectedVariables] = useState<string[]>([]);
  const [customPrompt, setCustomPrompt] = useState('');
  
  // AI configuration
  const [reasoningEffort, setReasoningEffort] = useState<ReasoningEffort>('none');
  const [enableChainOfThought, setEnableChainOfThought] = useState(false);
  const [fewShotExamples, setFewShotExamples] = useState<FewShotExample[]>([]);
  const [enableStreaming, setEnableStreaming] = useState(true);
  const [targetVariable, setTargetVariable] = useState('Q156_2010_5_slice');
  const [maxConcurrency, setMaxConcurrency] = useState(10);
  
  // Results and state
  const [results, setResults] = useState<SimulationResults | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentParameters, setCurrentParameters] = useState<SimulationRequest | null>(null);
  
  // Streaming state
  const [isStreaming, setIsStreaming] = useState(false);
  const [currentPersonaId, setCurrentPersonaId] = useState<number | null>(null);
  const [completedResponses, setCompletedResponses] = useState<AIResponse[]>([]);
  const [latestUpdate, setLatestUpdate] = useState<StreamingUpdate | undefined>();

  const getReasoningLabel = (effort: ReasoningEffort) => {
    switch (effort) {
      case 'none': return 'Fastest';
      case 'low': return 'Low';
      case 'medium': return 'Medium';
      case 'high': return 'High';
    }
  };

  const handleStreamingUpdate = useCallback((update: StreamingUpdate) => {
    setLatestUpdate(update);
    
    switch (update.type) {
      case 'progress':
        if (update.persona_id !== undefined) {
          setCurrentPersonaId(update.persona_id);
        }
        break;
        
      case 'persona_complete':
        if (update.response) {
          // Attach the persona_profile to the response
          const responseWithProfile: AIResponse = {
            ...update.response,
            persona_profile: update.persona_profile || update.response.persona_profile
          };
          setCompletedResponses(prev => [...prev, responseWithProfile]);
        }
        setCurrentPersonaId(null);
        break;
        
      case 'complete':
        if (update.results) {
          setResults(update.results);
        }
        setIsStreaming(false);
        setIsLoading(false);
        setCurrentPersonaId(null);
        break;
        
      case 'error':
        setError(update.error_message || 'An error occurred during simulation');
        setIsStreaming(false);
        setIsLoading(false);
        break;
    }
  }, []);

  const handleRunSimulation = async () => {
    setIsLoading(true);
    setError(null);
    setResults(null);
    setCompletedResponses([]);
    setCurrentPersonaId(null);
    
    const request: SimulationRequest = {
      sample_size: sampleSize,
      selected_variables: selectedVariables,
      custom_prompt: customPrompt || undefined,
      target_variable: targetVariable,
      model: reasoningEffort === 'none' || reasoningEffort === 'low' ? "gpt-5-nano" : "gpt-5",
      reasoning_effort: reasoningEffort,
      enable_chain_of_thought: enableChainOfThought,
      few_shot_examples: fewShotExamples.length > 0 ? fewShotExamples : undefined,
      max_concurrency: maxConcurrency,
    };
    
    setCurrentParameters(request);
    
    // GPT-5 always supports streaming
    const useStreaming = enableStreaming;
    
    try {
      if (useStreaming) {
        setIsStreaming(true);
        
        await apiService.runSimulationStreaming(
          request,
          handleStreamingUpdate,
          (err) => {
            setError(err.message);
            setIsStreaming(false);
            setIsLoading(false);
          }
        );
      } else {
        // Non-streaming mode
        const simulationResults = await apiService.runSimulation(request);
        setResults(simulationResults);
        setIsLoading(false);
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error 
        ? err.message 
        : 'Error during simulation execution';
      setError(errorMessage);
      setIsStreaming(false);
      setIsLoading(false);
      console.error('Simulation error:', err);
    }
  };

  const handleLoadExperiment = (experiment: Experiment) => {
    // Load experiment parameters
    setSampleSize(experiment.parameters.sample_size);
    setSelectedVariables(experiment.parameters.selected_variables);
    setCustomPrompt(experiment.parameters.custom_prompt || '');
    
    // Load AI configuration if available
    if (experiment.parameters.reasoning_effort) {
      setReasoningEffort(experiment.parameters.reasoning_effort);
    }
    if (experiment.parameters.enable_chain_of_thought !== undefined) {
      setEnableChainOfThought(experiment.parameters.enable_chain_of_thought);
    }
    if (experiment.parameters.few_shot_examples) {
      setFewShotExamples(experiment.parameters.few_shot_examples);
    }
    
    // Load experiment results
    setResults(experiment.results);
    setCurrentParameters(experiment.parameters as SimulationRequest);
    setError(null);
    setCompletedResponses([]);
  };

  return (
    <div className="min-h-screen bg-background text-foreground antialiased">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-14 items-center justify-between">
            <div className="flex items-center space-x-4">
              <h1 className="text-lg font-semibold tracking-tight">
                AI Survey Simulation
              </h1>
              <span className="hidden sm:inline-flex text-xs text-muted-foreground px-2 py-1 bg-muted rounded">
                {getReasoningLabel(reasoningEffort)}
              </span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="hidden md:flex items-center space-x-2 text-sm text-muted-foreground">
                <Activity className="h-4 w-4" />
                <span>Dashboard</span>
              </div>
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className="p-2 rounded-md hover:bg-muted transition-colors"
                aria-label="Toggle theme"
              >
                {isDarkMode ? (
                  <Sun className="h-4 w-4" />
                ) : (
                  <Moon className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Column - Controls */}
          <div className="lg:col-span-4 space-y-6">
            <DatasetInfo targetVariable={targetVariable} />
            <ParameterControls
              sampleSize={sampleSize}
              setSampleSize={setSampleSize}
              selectedVariables={selectedVariables}
              setSelectedVariables={setSelectedVariables}
              customPrompt={customPrompt}
              setCustomPrompt={setCustomPrompt}
              onRunSimulation={handleRunSimulation}
              isLoading={isLoading}
              reasoningEffort={reasoningEffort}
              setReasoningEffort={setReasoningEffort}
              enableChainOfThought={enableChainOfThought}
              setEnableChainOfThought={setEnableChainOfThought}
              fewShotExamples={fewShotExamples}
              setFewShotExamples={setFewShotExamples}
              enableStreaming={enableStreaming}
              setEnableStreaming={setEnableStreaming}
              targetVariable={targetVariable}
              setTargetVariable={setTargetVariable}
              maxConcurrency={maxConcurrency}
              setMaxConcurrency={setMaxConcurrency}
            />
            <ExperimentManager
              currentResults={results}
              currentParameters={currentParameters}
              onLoadExperiment={handleLoadExperiment}
            />
          </div>

          {/* Right Column - Results */}
          <div className="lg:col-span-8 space-y-6">
            {error && (
              <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <span className="text-destructive font-medium">Error</span>
                  </div>
                  <p className="text-sm text-destructive/90">{error}</p>
                </div>
              </div>
            )}
            
            {/* Streaming Progress */}
            {(isStreaming || (completedResponses.length > 0 && !results)) && (
              <StreamingProgress
                isStreaming={isStreaming}
                currentPersonaId={currentPersonaId}
                totalPersonas={sampleSize}
                completedResponses={completedResponses}
                latestUpdate={latestUpdate}
              />
            )}
            
            <ResultsDisplay results={results} allResponses={completedResponses.length > 0 ? completedResponses : undefined} />
            
            {results && results.ground_truth_distribution && (
              <div className="space-y-6">
                <BiasAnalysis results={results} />
                <ConfusionMatrix results={results} />
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t mt-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center space-y-1">
            <p className="text-sm text-muted-foreground">
              AI Survey Simulation — Research project on survey response prediction
            </p>
            <p className="text-xs text-muted-foreground">
              Dataset: Youth and Europe Survey 2024 (Istituto Toniolo)
              {enableChainOfThought && ' | Chain-of-Thought'}
              {fewShotExamples.length > 0 && ` | ${fewShotExamples.length} Few-Shot Examples`}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
